import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import { Save, Mail, Clock } from 'lucide-react';
import axios from 'axios';
import { GlassmorphicCard, SectionTitle, BodyText, SecondaryText } from '../styles/sharedStyles';

function OralBSettings({ apiBaseUrl, token }) {
  const theme = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Time fields as HH:MM format strings
  const [morningStart, setMorningStart] = useState('08:00');
  const [morningEnd, setMorningEnd] = useState('10:00');
  const [eveningStart, setEveningStart] = useState('20:00');
  const [eveningEnd, setEveningEnd] = useState('22:00');
  const [sendReminders, setSendReminders] = useState(true);
  const [recipients, setRecipients] = useState('');

  // Helper to convert HH:MM to hour and minute
  const parseTime = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return { hour: h, minute: m || 0 };
  };

  // Helper to convert hour and minute to HH:MM
  const formatTime = (hour, minute) => {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBaseUrl}/api/oralb/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const s = res.data;
      setSettings(s);
      
      // Convert separate hour/minute to combined time strings
      setMorningStart(formatTime(s.morning_start_hour ?? 8, s.morning_start_minute ?? 0));
      setMorningEnd(formatTime(s.morning_end_hour ?? 10, s.morning_end_minute ?? 0));
      setEveningStart(formatTime(s.evening_start_hour ?? 20, s.evening_start_minute ?? 0));
      setEveningEnd(formatTime(s.evening_end_hour ?? 22, s.evening_end_minute ?? 0));
      
      setSendReminders(s.send_reminders !== false);
      setRecipients(Array.isArray(s.recipients) ? s.recipients.join(', ') : '');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSendTestEmail = async () => {
    if (!token) return;
    setTestEmailLoading(true);
    setError(null);
    setTestEmailResult(null);
    try {
      const res = await axios.post(`${apiBaseUrl}/api/oralb/settings/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestEmailResult(res.data?.message || 'Test email sent.');
      setTimeout(() => setTestEmailResult(null), 5000);
    } catch (err) {
      setTestEmailResult(null);
      setError(err.response?.data?.detail || err.message || 'Failed to send test email.');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const recipientList = recipients
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter((e) => e && e.includes('@'));
      
      // Convert time strings back to hour/minute
      const ms = parseTime(morningStart);
      const me = parseTime(morningEnd);
      const es = parseTime(eveningStart);
      const ee = parseTime(eveningEnd);
      
      await axios.put(
        `${apiBaseUrl}/api/oralb/settings`,
        {
          morning_start_hour: ms.hour,
          morning_start_minute: ms.minute,
          morning_end_hour: me.hour,
          morning_end_minute: me.minute,
          evening_start_hour: es.hour,
          evening_start_minute: es.minute,
          evening_end_hour: ee.hour,
          evening_end_minute: ee.minute,
          send_reminders: sendReminders,
          recipients: recipientList,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <Typography sx={{ color: '#00d4ff' }}>Loading settings…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle sx={{ mb: 2 }}>Oral-B Settings</SectionTitle>
      <SecondaryText sx={{ mb: 3, display: 'block' }}>
        Set morning and evening brushing windows. If no brush is detected by the end of a window, the app sends a
        reminder email (using the app notification email config) to the addresses below.
      </SecondaryText>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved.
        </Alert>
      )}

      <GlassmorphicCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
          Morning window
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, display: 'block' }}>
              Start
            </Typography>
            <TextField
              type="time"
              size="small"
              value={morningStart}
              onChange={(e) => setMorningStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }} // 15 minutes
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                },
                '& input[type="time"]': {
                  color: '#fff',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, display: 'block' }}>
              End
            </Typography>
            <TextField
              type="time"
              size="small"
              value={morningEnd}
              onChange={(e) => setMorningEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                },
                '& input[type="time"]': {
                  color: '#fff',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                },
              }}
            />
          </Grid>
        </Grid>
      </GlassmorphicCard>

      <GlassmorphicCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
          Evening window
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, display: 'block' }}>
              Start
            </Typography>
            <TextField
              type="time"
              size="small"
              value={eveningStart}
              onChange={(e) => setEveningStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                },
                '& input[type="time"]': {
                  color: '#fff',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                },
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, display: 'block' }}>
              End
            </Typography>
            <TextField
              type="time"
              size="small"
              value={eveningEnd}
              onChange={(e) => setEveningEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                },
                '& input[type="time"]': {
                  color: '#fff',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                },
              }}
            />
          </Grid>
        </Grid>
      </GlassmorphicCard>

      <GlassmorphicCard sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
          Email Notifications
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={sendReminders}
              onChange={(e) => setSendReminders(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#00d4ff',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#00d4ff',
                },
              }}
            />
          }
          label={<Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>Send reminder emails when brushing is missed</Typography>}
          sx={{ mb: 2 }}
        />
        
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, display: 'block' }}>
          Email recipients (comma-separated)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="email@example.com, another@example.com"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiInputBase-input': {
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
          }}
        />
        
        <Button
          variant="outlined"
          startIcon={<Mail size={18} />}
          onClick={handleSendTestEmail}
          disabled={testEmailLoading || !sendReminders}
          sx={{
            borderColor: 'rgba(0, 212, 255, 0.4)',
            color: '#00d4ff',
            '&:hover': {
              borderColor: '#00d4ff',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {testEmailLoading ? 'Sending...' : 'Send Test Email'}
        </Button>
        {testEmailResult && (
          <Typography sx={{ color: '#2ecc71', mt: 1, fontSize: '0.875rem' }}>
            {testEmailResult}
          </Typography>
        )}
      </GlassmorphicCard>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Save size={18} />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            bgcolor: '#00d4ff',
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#00b8e6',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <SecondaryText sx={{ fontSize: '0.8rem' }}>
          💡 Tip: Set the "end" time to when you want to be reminded if you haven't brushed yet. For example, set morning end to 10:00 to get a reminder email at 10:00 if you haven't brushed in the morning.
        </SecondaryText>
      </Box>
    </Box>
  );
}

export default OralBSettings;
